/**
 * 1€ filter (Casiez et al. 2012) — adaptive low-pass filter that suppresses
 * high-frequency jitter at slow speeds while staying responsive at fast speeds.
 * Used here to smooth MediaPipe's per-frame landmark noise before driving the rig.
 *
 * Reference: https://gery.casiez.net/1euro/
 *
 * Tuning notes:
 *  - `min_cutoff` (Hz): the cutoff at zero velocity. Lower = smoother but laggier.
 *  - `beta`: how much the cutoff increases with the input's derivative. Higher =
 *     more responsive to fast motion at the cost of letting some jitter through.
 *  - `d_cutoff` (Hz): cutoff for the derivative estimator itself. Default 1.0
 *     works in most cases.
 *
 * Defaults are conservative for live-driving a humanoid rig at ~30Hz:
 * `min_cutoff = 1.0, beta = 0.05, d_cutoff = 1.0`.
 */
export interface OneEuroFilterParams {
  min_cutoff?: number
  beta?: number
  d_cutoff?: number
}

export class OneEuroFilter {
  private readonly min_cutoff: number
  private readonly beta: number
  private readonly d_cutoff: number

  private prev_value: number | null = null
  private prev_derivative: number = 0
  private prev_time: number | null = null

  constructor (params: OneEuroFilterParams = {}) {
    this.min_cutoff = params.min_cutoff ?? 1.0
    this.beta = params.beta ?? 0.05
    this.d_cutoff = params.d_cutoff ?? 1.0
  }

  /**
   * Apply the filter. `time` is in seconds.
   *
   * `confidence_weight` (default 1) scales the smoothing alpha multiplicatively:
   * 1.0 = behave normally; values < 1 trust `prev_value` more, used to dampen
   * frames where the upstream landmark detector reported low visibility.
   * Pass `Math.pow(visibility, smoothing_strength)` to make low-confidence
   * frames react less to new noisy inputs while still updating internal state.
   */
  filter (value: number, time: number, confidence_weight: number = 1): number {
    if (this.prev_value === null || this.prev_time === null) {
      this.prev_value = value
      this.prev_time = time
      this.prev_derivative = 0
      return value
    }

    const dt = time - this.prev_time
    if (dt <= 0) return this.prev_value

    // Estimate derivative and low-pass-smooth it
    const derivative = (value - this.prev_value) / dt
    const a_d = OneEuroFilter.smoothing_factor(dt, this.d_cutoff)
    const filtered_derivative = OneEuroFilter.lowpass(derivative, this.prev_derivative, a_d)

    // Adaptive cutoff: bigger when motion is fast (gives less smoothing, less lag)
    const cutoff = this.min_cutoff + this.beta * Math.abs(filtered_derivative)
    let a = OneEuroFilter.smoothing_factor(dt, cutoff)
    // Confidence weighting: scale alpha down on low-confidence samples so the
    // filter favors prev_value. Clamped to [0, 1] to stay a valid lowpass.
    if (confidence_weight !== 1) {
      a = Math.max(0, Math.min(1, a * confidence_weight))
    }
    const filtered_value = OneEuroFilter.lowpass(value, this.prev_value, a)

    this.prev_value = filtered_value
    this.prev_derivative = filtered_derivative
    this.prev_time = time
    return filtered_value
  }

  /** Reset internal state. Use when the input source restarts (e.g., webcam re-acquired). */
  reset (): void {
    this.prev_value = null
    this.prev_derivative = 0
    this.prev_time = null
  }

  private static lowpass (x: number, prev: number, alpha: number): number {
    return alpha * x + (1 - alpha) * prev
  }

  private static smoothing_factor (dt: number, cutoff: number): number {
    const r = 2 * Math.PI * cutoff * dt
    return r / (r + 1)
  }
}

/**
 * 4-tuple version for quaternions: filter each component, then renormalize.
 * Component-wise filtering is mathematically inferior to slerp-based smoothing
 * but cheap and robust enough for hobbyist mocap; renormalization keeps unit length.
 */
export class QuaternionOneEuroFilter {
  private readonly fx: OneEuroFilter
  private readonly fy: OneEuroFilter
  private readonly fz: OneEuroFilter
  private readonly fw: OneEuroFilter
  /** Tracks the previous filtered quaternion so we can flip the sign of incoming
   *  values when they're on the opposite hemisphere (q and -q represent the same
   *  rotation, but mixing them numerically causes tumbling). */
  private prev_filtered: [number, number, number, number] | null = null

  constructor (params: OneEuroFilterParams = {}) {
    this.fx = new OneEuroFilter(params)
    this.fy = new OneEuroFilter(params)
    this.fz = new OneEuroFilter(params)
    this.fw = new OneEuroFilter(params)
  }

  filter (
    q: [number, number, number, number],
    time: number,
    confidence_weight: number = 1
  ): [number, number, number, number] {
    let [x, y, z, w] = q

    // Hemisphere correction: if dot(prev, current) < 0, flip the input quaternion.
    if (this.prev_filtered !== null) {
      const [px, py, pz, pw] = this.prev_filtered
      const dot = px * x + py * y + pz * z + pw * w
      if (dot < 0) {
        x = -x; y = -y; z = -z; w = -w
      }
    }

    const fx = this.fx.filter(x, time, confidence_weight)
    const fy = this.fy.filter(y, time, confidence_weight)
    const fz = this.fz.filter(z, time, confidence_weight)
    const fw = this.fw.filter(w, time, confidence_weight)

    // Renormalize. Filtering breaks unit-length; without renormalization we'd
    // accumulate scale drift in the rig.
    const len = Math.sqrt(fx * fx + fy * fy + fz * fz + fw * fw)
    const out: [number, number, number, number] = len > 1e-9
      ? [fx / len, fy / len, fz / len, fw / len]
      : [0, 0, 0, 1]

    this.prev_filtered = out
    return out
  }

  reset (): void {
    this.fx.reset()
    this.fy.reset()
    this.fz.reset()
    this.fw.reset()
    this.prev_filtered = null
  }
}
